import isEqual from 'lodash/isEqual';

export interface DraftChange {
    field: string;
    before: string;
    after: string;
}

const getValueAtPath = (value: any, path: string) =>
    path.split('.').reduce((current, segment) => current?.[segment], value);

const summarizeValue = (value: unknown): string => {
    if (value === undefined || value === null || value === '') {return 'None';}

    if (Array.isArray(value)) {
        if (!value.length) {return 'None';}

        return value
            .map((item) => {
                if (item && typeof item === 'object' && 'field' in item) {
                    return String((item as { field?: unknown }).field ?? 'Unknown');
                }

                return summarizeValue(item);
            })
            .join(', ');
    }

    if (typeof value === 'object') {
        return JSON.stringify(value);
    }

    return String(value);
};

const trackedConfigurationFields = [
    { path: 'dataSet.type', label: 'Dataset' },
    { path: 'dataSet.key', label: 'Dataset Category' },
    { path: 'query.metrics', label: 'Metrics' },
    { path: 'query.dimensions', label: 'Dimensions' },
    { path: 'query.filters', label: 'Filters' },
    { path: 'query.dateRange', label: 'Date Range' },
    { path: 'formatting', label: 'Formatting' },
    { path: 'options', label: 'Options' },
    { path: 'titleOverride', label: 'Title' },
    { path: 'descriptionOverride', label: 'Description' },
];

export const getDraftChanges = (original: any, draft: any, draftProperties?: Record<string, unknown>) => {
    const originalConfiguration = original?.configuration ?? {};
    const draftConfiguration = draft ?? {};

    const propertyChanges: DraftChange[] = [
        { key: 'type', label: 'Component Type' },
        { key: 'embedURL', label: 'Embed URL' },
    ]
        .map(({ key, label }) => {
            const before = original?.[key];
            const after = draftProperties?.[key] ?? before;

            if (isEqual(before, after)) {return undefined;}

            return {
                field: label,
                before: summarizeValue(before),
                after: summarizeValue(after),
            };
        })
        .filter((change): change is DraftChange => Boolean(change));

    const changes: DraftChange[] = trackedConfigurationFields
        .map(({ path, label }) => {
            const before = getValueAtPath(originalConfiguration, path);
            const after = getValueAtPath(draftConfiguration, path);

            if (isEqual(before, after)) {return undefined;}

            return {
                field: label,
                before: summarizeValue(before),
                after: summarizeValue(after),
            };
        })
        .filter((change): change is DraftChange => Boolean(change));

    return [...propertyChanges, ...changes];
};
